import { FieldTypes } from '../../../constants/FieldTypes'
import {
  CheckboxField,
  DropdownField,
  FieldMap,
  FileField,
  NumberField,
  TextAreaField,
  TextField,
} from '../../../types/forms/fields'
import {
  UltrasoundData,
  UltrasoundDuctusVenosus,
  UltrasoundFetalBiometry,
  UltrasoundPlacentalLocation,
  UltrasoundStudyType,
  UltrasoundUaEdFlow,
} from '../types/ultrasound'

const studyDateField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Study Date',
}

const gaAtStudyDaysField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'GA at Study (days)',
  hints: [{ text: 'Auto-populated from EDD + study date. Edit if different.' }],
}

const orderingProviderField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Ordering Provider',
}

const studyTypeField: DropdownField<UltrasoundStudyType> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Study Type',
  options: [
    { id: 'limited-pocus', text: 'Limited POCUS' },
    { id: 'standard-ob', text: 'Standard OB' },
    { id: 'detailed', text: 'Detailed' },
    { id: 'bpp', text: 'BPP' },
    { id: 'nst-us', text: 'NST + Ultrasound' },
    { id: 'transvaginal', text: 'Transvaginal' },
  ],
}

const indicationField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Indication for Study',
  validate: (v: unknown) =>
    typeof v === 'string' && v.trim().toLowerCase() === 'routine'
      ? 'Specify clinical indication — "Routine" is not acceptable'
      : undefined,
  hints: [{ text: 'Required. "Routine" is not an acceptable indication.' }],
}

const deviceEquipmentField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Device / Equipment Used',
}

const imagesArchivedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Images archived in the medical record.',
}

const imageFileField: FileField = {
  type: FieldTypes.FILE,
  name: 'Ultrasound Image',
  placeholder: 'Ultrasound Image',
  fileType: 'image',
}

export const ultrasoundHeaderField: FieldMap = {
  name: 'Ultrasound Report Header',
  children: {
    studyDate: studyDateField,
    gaAtStudyDays: gaAtStudyDaysField,
    orderingProvider: orderingProviderField,
    studyType: studyTypeField,
    indication: indicationField,
    deviceEquipment: deviceEquipmentField,
    imagesArchived: imagesArchivedField,
    imageFile: imageFileField,
  },
}

// §2.14.2 Scenario A findings fields

const fetalCardiacActivityField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Fetal Cardiac Activity',
  options: [
    { id: 'present', text: 'Present' },
    { id: 'absent', text: 'Absent' },
    { id: 'not-assessed', text: 'Not Assessed' },
  ],
}

const fetalNumberField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Fetal Number',
  options: [
    { id: 'singleton', text: 'Singleton' },
    { id: 'twins', text: 'Twins' },
    { id: 'higher-order', text: 'Higher Order' },
  ],
}

const fetalPresentationField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Fetal Presentation',
  options: [
    { id: 'cephalic', text: 'Cephalic' },
    { id: 'breech', text: 'Breech' },
    { id: 'transverse', text: 'Transverse' },
    { id: 'oblique', text: 'Oblique' },
  ],
}

const placentalLocationField: DropdownField<UltrasoundPlacentalLocation> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Placental Location',
  options: [
    { id: 'anterior', text: 'Anterior' },
    { id: 'posterior', text: 'Posterior' },
    { id: 'fundal', text: 'Fundal' },
    { id: 'lateral', text: 'Lateral' },
    { id: 'low-lying', text: 'Low-Lying' },
    { id: 'previa', text: 'Placenta Previa' },
  ],
}

const amnioticFluidTypeField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Amniotic Fluid Measurement',
  inputType: 'radio',
  options: [
    { id: 'afi', text: 'AFI (cm)' },
    { id: 'sdp', text: 'SDP (cm)' },
  ],
}

const amnioticFluidValueField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Value (cm)',
  condition: v => !!v?.amnioticFluidType,
}

const biometryBpdField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'BPD (mm)',
}

const biometryHcField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'HC (mm)',
}

const biometryAcField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'AC (mm)',
}

const biometryFlField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'FL (mm)',
}

const biometryEfwField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'EFW (g) — auto-calculated',
}

export const fetalBiometryField: FieldMap = {
  name: 'Fetal Biometry',
  children: {
    bpd: biometryBpdField,
    hc: biometryHcField,
    ac: biometryAcField,
    fl: biometryFlField,
    efw: biometryEfwField,
  },
}

// §2.14.2 Doppler studies fields

const uaPiField: NumberField = { type: FieldTypes.NUMBER, placeholder: 'UA PI' }
const uaRiField: NumberField = { type: FieldTypes.NUMBER, placeholder: 'UA RI' }
const uaSdRatioField: NumberField = { type: FieldTypes.NUMBER, placeholder: 'UA S/D Ratio' }
const uaEdFlowField: DropdownField<UltrasoundUaEdFlow> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'UA End-Diastolic Flow',
  options: [
    { id: 'present', text: 'Present' },
    { id: 'absent', text: 'Absent' },
    { id: 'reversed', text: 'Reversed' },
  ],
}
const mcaPiField: NumberField = { type: FieldTypes.NUMBER, placeholder: 'MCA PI' }
const mcaPsvField: NumberField = { type: FieldTypes.NUMBER, placeholder: 'MCA PSV (cm/s)' }
const cprField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'CPR (MCA PI ÷ UA PI — auto-calculated)',
}
const ductusVenosusField: DropdownField<UltrasoundDuctusVenosus> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Ductus Venosus',
  options: [
    { id: 'normal', text: 'Normal' },
    { id: 'abnormal', text: 'Abnormal' },
    { id: 'absent-a-wave', text: 'Absent a-wave' },
    { id: 'reversed-a-wave', text: 'Reversed a-wave' },
  ],
}

export const dopplerStudyField: FieldMap = {
  name: 'Doppler Studies',
  children: {
    uaPi: uaPiField,
    uaRi: uaRiField,
    uaSdRatio: uaSdRatioField,
    uaEdFlow: uaEdFlowField,
    mcaPi: mcaPiField,
    mcaPsv: mcaPsvField,
    cpr: cprField,
    ductusVenosus: ductusVenosusField,
  },
}

const cervicalLengthField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Cervical Length (mm)',
}

const anatomySurveyCompleteField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Fetal anatomy survey completed.',
}

const clinicalImpressionField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Clinical Impression',
  hints: [{ text: 'Required. Based on independent clinical judgment.' }],
}

const referralIndicatedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Referral indicated based on findings.',
}

const followUpStudyIndicatedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Follow-up study indicated.',
}

const clinicalImpressionSummaryField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Clinical Impression Summary (auto-generated, editable)',
}

export const ultrasoundFindingsField: FieldMap = {
  name: 'Ultrasound Findings',
  children: {
    fetalCardiacActivity: fetalCardiacActivityField,
    fetalNumber: fetalNumberField,
    fetalPresentation: fetalPresentationField,
    placentalLocation: placentalLocationField,
    amnioticFluidType: amnioticFluidTypeField,
    amnioticFluidValue: amnioticFluidValueField,
    fetalBiometry: fetalBiometryField,
    dopplerStudy: dopplerStudyField,
    cervicalLength: cervicalLengthField,
    anatomySurveyComplete: anatomySurveyCompleteField,
    clinicalImpression: clinicalImpressionField,
    referralIndicated: referralIndicatedField,
    followUpStudyIndicated: followUpStudyIndicatedField,
    clinicalImpressionSummary: clinicalImpressionSummaryField,
  },
}

// §2.14.3 Scenario B fields — rendered conditionally when scenario === 'sonographer-study'

const imageQualityField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Image Quality',
  options: [
    { id: 'optimal', text: 'Optimal' },
    { id: 'adequate', text: 'Adequate' },
    { id: 'limited-habitus', text: 'Limited — Habitus' },
    { id: 'limited-position', text: 'Limited — Fetal Position' },
    { id: 'limited-other', text: 'Limited — Other' },
  ],
}

const agreementWithFindingsField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Agreement with Technical Findings',
  options: [
    { id: 'agree', text: 'Agree' },
    { id: 'disagree', text: 'Disagree' },
  ],
}

const disagreementDetailField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Describe areas of disagreement',
  condition: v => (v as any)?.agreementWithFindings === 'disagree',
}

export const ultrasoundScenarioBField: FieldMap = {
  name: 'Sonographer Study',
  children: {
    imageQuality: imageQualityField,
    agreementWithFindings: agreementWithFindingsField,
    disagreementDetail: disagreementDetailField,
  },
}

// §2.14.2: Hadlock 1985 formula — inputs in mm, output in grams.
// Requires at minimum AC + FL; BPD is incorporated when present.
export const computeHadlockEfw = (b: UltrasoundFetalBiometry): number | null => {
  const { bpd, ac, fl } = b
  if (!ac || !fl) return null
  const logEfw =
    1.4787 -
    0.003343 * (ac / 10) * (fl / 10) +
    (bpd ? 0.001837 * (bpd / 10) ** 2 : 0) +
    0.0458 * (ac / 10) +
    0.158 * (fl / 10)
  return Math.round(10 ** logEfw)
}

// §2.14.2: Hadlock 1991 EFW normative table — mean = ln(50th-percentile EFW in grams), SD = 0.178 (constant).
// Source: Hadlock FP et al., Radiology 1991;181:129-133. Covers 14–42w.
const HADLOCK_EFW_NORMS: ReadonlyArray<{ week: number; mean: number; sd: number }> = [
  { week: 14, mean: 4.5326, sd: 0.178 },
  { week: 15, mean: 4.7622, sd: 0.178 },
  { week: 16, mean: 4.9836, sd: 0.178 },
  { week: 17, mean: 5.1985, sd: 0.178 },
  { week: 18, mean: 5.4072, sd: 0.178 },
  { week: 19, mean: 5.6131, sd: 0.178 },
  { week: 20, mean: 5.8021, sd: 0.178 },
  { week: 21, mean: 5.9889, sd: 0.178 },
  { week: 22, mean: 6.1695, sd: 0.178 },
  { week: 23, mean: 6.342, sd: 0.178 },
  { week: 24, mean: 6.5073, sd: 0.178 },
  { week: 25, mean: 6.6659, sd: 0.178 },
  { week: 26, mean: 6.8167, sd: 0.178 },
  { week: 27, mean: 6.9613, sd: 0.178 },
  { week: 28, mean: 7.0987, sd: 0.178 },
  { week: 29, mean: 7.229, sd: 0.178 },
  { week: 30, mean: 7.3511, sd: 0.178 },
  { week: 31, mean: 7.4672, sd: 0.178 },
  { week: 32, mean: 7.5771, sd: 0.178 },
  { week: 33, mean: 7.6789, sd: 0.178 },
  { week: 34, mean: 7.7741, sd: 0.178 },
  { week: 35, mean: 7.8621, sd: 0.178 },
  { week: 36, mean: 7.9424, sd: 0.178 },
  { week: 37, mean: 8.0157, sd: 0.178 },
  { week: 38, mean: 8.0822, sd: 0.178 },
  { week: 39, mean: 8.1421, sd: 0.178 },
  { week: 40, mean: 8.1943, sd: 0.178 },
  { week: 41, mean: 8.2398, sd: 0.178 },
  { week: 42, mean: 8.2779, sd: 0.178 },
]

// Abramowitz & Stegun 26.2.17 rational approximation — max error < 7.5e-8.
function normalCdf(x: number): number {
  if (x < 0) return 1 - normalCdf(-x)
  const t = 1 / (1 + 0.2316419 * x)
  const d = 0.398942282 * Math.exp(-0.5 * x * x)
  const poly =
    t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))))
  return 1 - d * poly
}

// §2.14.2: Returns EFW percentile (1–99) based on Hadlock 1991. gaWeeks = complete weeks (14–42).
// Returns null when GA is out of table range or efwGrams ≤ 0.
export function getEfwPercentile(efwGrams: number, gaWeeks: number): number | null {
  if (efwGrams <= 0) return null
  const row = HADLOCK_EFW_NORMS.find(r => r.week === gaWeeks)
  if (!row) return null
  const z = (Math.log(efwGrams) - row.mean) / row.sd
  return Math.max(1, Math.min(99, Math.round(normalCdf(z) * 100)))
}

export function suggestUltrasoundIcdCodes(data: Partial<UltrasoundData>): string[] {
  const codes: string[] = []
  const ga = data.gaAtStudyDays ?? 0
  const trimester = ga < 98 ? 1 : ga < 196 ? 2 : 3

  // Fetal presentation
  if (data.fetalPresentation === 'breech') codes.push('O32.1XX0')
  else if (data.fetalPresentation === 'transverse' || data.fetalPresentation === 'oblique')
    codes.push('O32.2XX0')

  // Placental location
  if (data.placentalLocation === 'previa')
    codes.push(trimester === 1 ? 'O43.101' : trimester === 2 ? 'O43.102' : 'O43.103')
  else if (data.placentalLocation === 'low-lying') codes.push('O44.01')

  // Amniotic fluid — oligohydramnios (AFI < 5 cm or SDP < 2 cm)
  const isOligo =
    (data.amnioticFluidType === 'afi' && (data.amnioticFluidValue ?? Infinity) < 5) ||
    (data.amnioticFluidType === 'sdp' && (data.amnioticFluidValue ?? Infinity) < 2)
  if (isOligo) codes.push(trimester === 1 ? 'O41.00X0' : trimester === 2 ? 'O41.02X0' : 'O41.03X0')

  // Amniotic fluid — polyhydramnios (AFI > 25 cm or SDP > 8 cm)
  const isPoly =
    (data.amnioticFluidType === 'afi' && (data.amnioticFluidValue ?? -Infinity) > 25) ||
    (data.amnioticFluidType === 'sdp' && (data.amnioticFluidValue ?? -Infinity) > 8)
  if (isPoly) codes.push(trimester === 1 ? 'O40.1XX0' : trimester === 2 ? 'O40.2XX0' : 'O40.3XX0')

  // Short cervix — clinically meaningful ≥ 12w (84 days); standard threshold < 25 mm
  if (data.cervicalLength != null && data.cervicalLength < 25 && ga >= 84)
    codes.push(trimester <= 2 ? 'O26.872' : 'O26.873')

  // Absent fetal cardiac activity
  if (data.fetalCardiacActivity === 'absent') codes.push('O36.4XX0')

  // Doppler abnormality → placental insufficiency (O36.511–513, trimester-keyed)
  const dop = data.dopplerStudy
  const dopplerAbnormal =
    dop?.uaEdFlow === 'absent' ||
    dop?.uaEdFlow === 'reversed' ||
    (dop?.cpr != null && dop.cpr < 1.0) ||
    dop?.ductusVenosus === 'absent-a-wave' ||
    dop?.ductusVenosus === 'reversed-a-wave'
  if (dopplerAbnormal)
    codes.push(trimester === 1 ? 'O36.511' : trimester === 2 ? 'O36.512' : 'O36.513')

  return codes
}
